import { cn, formatDecimal } from '@/lib/utils';

// Solid marker colour of each series (the gradient's dark stop, amber's base).
const MARKER_COLORS = {
  accumulated: 'var(--chart-graphite)',
  yesterday: 'var(--chart-2-to)',
  runRate: 'var(--chart-1)',
} as const;

// Separates markers that sit on top of each other, as the system does between
// chart segments: a border in the colour of the surface behind.
const MARKER_CLASS =
  'absolute top-1/2 w-[9px] h-[9px] rounded-full box-content border-2 border-[color:var(--surface-card)]';

interface PacingGaugeProps {
  accumulated: number;
  yesterday: number | null;
  runRate: number;
  className?: string;
}

export function PacingGauge({ accumulated, yesterday, runRate, className }: PacingGaugeProps) {
  // Clamp values for display (0-200%)
  const clampValue = (v: number) => Math.min(200, Math.max(0, v));

  const accPosition = (clampValue(accumulated) / 200) * 100;
  const rrPosition = (clampValue(runRate) / 200) * 100;
  const ystPosition = yesterday !== null ? (clampValue(yesterday) / 200) * 100 : null;

  return (
    <div className={cn('w-full', className)}>
      <div className="flex flex-col xs:flex-row xs:justify-between text-adv-body-s text-adv-secondary mb-2 gap-1 xs:gap-0">
        <span className="flex items-center gap-1">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: MARKER_COLORS.accumulated }}
          />
          <span className="hidden xs:inline">Acc:</span>
          <span className="xs:hidden">Accumulated:</span>{' '}
          <span className="adv-num font-bold text-adv-primary">{formatDecimal(accumulated, 1)}%</span>
        </span>
        <span className="flex items-center gap-1">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: MARKER_COLORS.yesterday }}
          />
          Yesterday:{' '}
          <span className="adv-num font-bold text-adv-primary">
            {yesterday !== null ? `${formatDecimal(yesterday, 1)}%` : '-'}
          </span>
        </span>
        <span className="flex items-center gap-1">
          <span
            className="w-2 h-2 rounded-full flex-shrink-0"
            style={{ backgroundColor: MARKER_COLORS.runRate }}
          />
          <span className="hidden xs:inline">RR:</span>
          <span className="xs:hidden">Run Rate:</span>{' '}
          <span className="adv-num font-bold text-adv-primary">{formatDecimal(runRate, 1)}%</span>
        </span>
      </div>

      {/* Rail of the design system's timeline: a 2px line with 9px markers */}
      <div className="relative h-4">
        <div className="absolute inset-x-0 top-1/2 h-0.5 -translate-y-1/2 bg-adv-border-default" />

        {/* Target at 100% */}
        <div
          className="absolute top-1/2 h-3 w-0.5 bg-adv-secondary"
          style={{ left: '50%', transform: 'translate(-50%, -50%)' }}
        />

        {ystPosition !== null && (
          <div
            className={MARKER_CLASS}
            style={{
              left: `${ystPosition}%`,
              transform: 'translate(-50%, -50%)',
              backgroundColor: MARKER_COLORS.yesterday,
            }}
          />
        )}

        <div
          className={MARKER_CLASS}
          style={{
            left: `${accPosition}%`,
            transform: 'translate(-50%, -50%)',
            backgroundColor: MARKER_COLORS.accumulated,
          }}
        />

        <div
          className={MARKER_CLASS}
          style={{
            left: `${rrPosition}%`,
            transform: 'translate(-50%, -50%)',
            backgroundColor: MARKER_COLORS.runRate,
          }}
        />
      </div>

      <div className="adv-num flex justify-between text-adv-code-xs text-adv-tertiary mt-1">
        <span>0%</span>
        <span>100%</span>
        <span>200%</span>
      </div>
    </div>
  );
}
